import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { DropdownModule } from "primeng/dropdown";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
    AssessmentService,
    FlightService,
    StatsFlightFilter,
    StatsService,
} from "@/_services";
import { combineLatest, Observable, of, Subscription } from "rxjs";
import { AsyncPipe, CommonModule } from "@angular/common";
import { DateTimeFormat } from "@/_utility/DateTimeFormat";
import {
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
} from "rxjs/operators";
import { Options } from "chartjs-plugin-datalabels/types/options";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { StatsSummary } from "@/components/shared/graphics/graphic-interfaces";
import { buildPastDaysDateRange } from "@/_utility/date.utils";

const lightChartLabelColor = "var(--chart-text)";

const labelOptions: Options = {
    color: lightChartLabelColor,
    font: {
        weight: "bold",
        size: 14,
    },
    display: (context) => {
        const point = context.dataset.data[context.dataIndex] as number;
        return point > 0;
    },
};

@UntilDestroy()
@Component({
    selector: "app-home-crew-performance",
    standalone: true,
    imports: [
        CommonModule,
        SharedComponentsModule,
        DropdownModule,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
    ],
    templateUrl: "./home-crew-performance.component.html",
    styleUrl: "./home-crew-performance.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeCrewPerformanceComponent implements OnInit, OnDestroy {
    private flightService = inject(FlightService);
    private AssessmentService = inject(AssessmentService);
    private statsService = inject(StatsService);

    @Input() isAdmin: boolean = false;

    readonly DateTimeFormat = DateTimeFormat;
    timeFrameList: RadioOption[] = [
        { label: "Past 7 Days", value: 7 },
        { label: "Past 30 Days", value: 30 },
        { label: "Past 60 Days", value: 60 },
        { label: "Past 90 Days", value: 90 },
        { label: "Past Year", value: 365 },
    ];
    timeFrameControl: FormControl<number> = new FormControl<number>(30);
    riskAssessmentOptions$: Observable<{ label: string; value: string }[]>;
    riskAssessmentControl = new FormControl(null);
    timeFrameSubscription: Subscription;

    stats$: Observable<any>;

    readonly ChartOptions = {
        type: "line",
        plugins: {
            datalabels: labelOptions,
        },
        legend: { display: false },
        aspectRatio: 20,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5,
            },
        },
        scales: {
            yAxes: [
                {
                    ticks: {
                        callback: function (value, index, values) {
                            return index === 0 || index === values.length - 1
                                ? value
                                : "";
                        },
                        beginAtZero: true,
                        stepSize: 5,
                        autoSkip: false,
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    ticks: {
                        display: false, // ⬅️ Esto oculta los labels del eje X
                    },
                },
            ],
        },
    };
    averageScoreDataSet$: Observable<StatsSummary>;
    completionDataset$: Observable<StatsSummary>;

    ngOnInit() {
        this.riskAssessmentOptions$ =
            this.AssessmentService.getAssessmentsByType("assessment").pipe(
                untilDestroyed(this),
                map((assessments) => {
                    return assessments.map((assessment) => {
                        return {
                            label: assessment.title,
                            value: assessment._id,
                        };
                    });
                }),
                tap((options) => {
                    if (
                        !this.riskAssessmentControl.value &&
                        options.length > 0
                    ) {
                        this.riskAssessmentControl.setValue(options[0].value);
                    }
                }),
                shareReplay(1),
            );
        this.stats$ = combineLatest([
            this.timeFrameControl.valueChanges.pipe(
                startWith(this.timeFrameControl.value),
            ),
            this.riskAssessmentControl.valueChanges.pipe(
                startWith(this.riskAssessmentControl.value),
            ),
        ]).pipe(
            untilDestroyed(this),
            switchMap(([timeFrameValue, formType]) => {
                const filterParams: StatsFlightFilter = {
                    flightDate: [],
                    assessment: null,
                };

                if (timeFrameValue) {
                    const [start, end] = buildPastDaysDateRange(timeFrameValue);
                    filterParams.flightDate = [start, end];
                }

                // Always include formType - use current value or get first option
                let formTypeToUse = formType;
                if (!formTypeToUse) {
                    // If no formType, wait for options to load and use the first one
                    return this.riskAssessmentOptions$.pipe(
                        take(1),
                        switchMap((options) => {
                            if (
                                options.length > 0 &&
                                !this.riskAssessmentControl.value
                            ) {
                                this.riskAssessmentControl.setValue(
                                    options[0].value,
                                );
                                formTypeToUse = options[0].value;
                            } else {
                                formTypeToUse =
                                    this.riskAssessmentControl.value;
                            }

                            if (formTypeToUse) {
                                filterParams.assessment = formTypeToUse;
                            }

                            return this.statsService.getStats(filterParams);
                        }),
                    );
                } else {
                    filterParams.assessment = formTypeToUse;
                    return this.statsService.getStats(filterParams);
                }
            }),
            shareReplay(1),
            map((stats) => {
                // this is a temporal fix to handle the new API structure for the demo purposes also we need to merge asap performance and crew performance to avoid duplicate code
                if (Array.isArray(stats.data)) {
                    const selectedId = this.riskAssessmentControl.value;
                    const match = stats.data.find(
                        (item: any) => item.assessmentId === selectedId,
                    );
                    return {
                        ...stats,
                        data: match?.summary || stats.data[0]?.summary || {},
                    };
                }
                return stats;
            }),
            tap((stats) => {
                this.averageScoreDataSet$ = of(
                    stats.data.averageRiskScoreOverTime?.chartData,
                );
                this.completionDataset$ = of(
                    stats.data.completionRiskRateOverTime?.chartData,
                );
            }),
        );
    }

    ngOnDestroy() {
        this.timeFrameSubscription?.unsubscribe();
    }
}
